import React from 'react';

import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useSubmitClaims } from '~/hooks/apis/loyalty/useSubmitClaims';
import useCurrentUser from '~/hooks/useCurrentUser';

import type { BulkClaimResult, ClaimCampaignContract, ClaimCsvRow } from '~/services/LuxLoyalty';

interface Props {
  rows: Array<ClaimCsvRow>;
  campaign: ClaimCampaignContract;
  enrolMember?: boolean;
  onBack: () => void;
  onSubmitSuccess: (response: BulkClaimResult) => void;
}

const MAX_PREVIEW_ROWS = 5;

export default function ReviewStep({ rows, campaign, enrolMember, onBack, onSubmitSuccess }: Props) {
  const { user } = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();

  const { mutate, isPending, progress } = useSubmitClaims({
    onSuccess: (data) => {
      const msg =
        data.failedCount > 0
          ? `Redemptions processed: ${data.successCount} succeeded, ${data.failedCount} failed`
          : 'Redemptions submitted successfully';
      enqueueSnackbar(msg, { variant: data.failedCount > 0 ? 'warning' : 'success' });
      onSubmitSuccess(data);
    },
    onError: (error) => {
      enqueueSnackbar(`Submission failed: ${error.message}`, { variant: 'error' });
    },
  });

  const previewRows = rows.slice(0, MAX_PREVIEW_ROWS);
  const hasCodeColumn = rows.some((r) => r.code);
  const progressPercent = progress.total > 0 ? (progress.processed / progress.total) * 100 : 0;

  const handleSubmit = () => {
    mutate({ campaignId: campaign.id, rows, enrolMember });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Confirm Redemptions
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Please review the redemptions before submitting.
      </Typography>

      {/* Admin info */}
      <Stack direction="row" spacing={3} mb={3}>
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            INITIATED BY
          </Typography>
          <Typography variant="body2">{user?.fullName || 'Unknown'}</Typography>
        </Box>
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            TIMESTAMP
          </Typography>
          <Typography variant="body2">{new Date().toLocaleString()}</Typography>
        </Box>
      </Stack>

      {/* Campaign info */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Campaign
        </Typography>
        <Typography variant="body2">{campaign.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          Validator: {campaign.validatorType}
        </Typography>
      </Box>

      {/* Total affected */}
      <Box
        sx={{
          border: 1,
          borderColor: 'primary.main',
          borderRadius: 1,
          p: 2,
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="subtitle1">Total Customers</Typography>
          <Typography variant="body2" color="text.secondary">
            Total batch size selected for processing
          </Typography>
        </Box>
        <Typography variant="h3" color="primary">
          {rows.length}
        </Typography>
      </Box>

      {/* Detailed changes table */}
      <Typography variant="subtitle1" gutterBottom>
        Customer List
        <Typography component="span" variant="body2" color="text.secondary" ml={2}>
          Showing top {previewRows.length} of {rows.length}
        </Typography>
      </Typography>

      <TableContainer sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Customer ID</TableCell>
              {hasCodeColumn && <TableCell>Code</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {previewRows.map((row) => (
              <TableRow key={row.customerId}>
                <TableCell>
                  <Typography variant="body2">{row.customerId}</Typography>
                </TableCell>
                {hasCodeColumn && (
                  <TableCell>
                    <Typography variant="body2">{row.code || '-'}</Typography>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Progress bar */}
      {isPending && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" mb={1}>
            Processing {progress.processed} of {progress.total}...
          </Typography>
          <LinearProgress variant="determinate" value={progressPercent} />
        </Box>
      )}

      {/* Actions */}
      <Stack direction="row" justifyContent="center" spacing={2}>
        <Button variant="text" onClick={onBack} disabled={isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isPending}>
          {isPending ? 'Submitting...' : 'Confirm & Submit'}
        </Button>
      </Stack>
    </Box>
  );
}
